import { Controller, useFormContext } from 'react-hook-form';

import { AddressAutocompleteInput } from '@/components/AddressAutocompleteInput';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { insuranceStateOptions } from '@/lib/zipcode';
import type { ShippingAddressFormData } from '@/types/user-profile.type';

export function ShippingAddressFormFields({ formId }: { formId: string }) {
  const { control, setValue } = useFormContext<ShippingAddressFormData>();

  return (
    <div className="space-y-7">
      <Controller
        name="addressLine1"
        control={control}
        render={({ field, fieldState }) => {
          const inputId = `${formId}-addressLine1`;

          return (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={inputId}>Mailing address</FieldLabel>
              <AddressAutocompleteInput
                {...field}
                onValueChange={field.onChange}
                onValueSelect={(address) => {
                  field.onChange(address.street_line);
                  setValue('addressLine2', address.secondary, { shouldValidate: true });
                  setValue('city', address.city, { shouldValidate: true });
                  setValue('state', address.state, { shouldValidate: true });
                  setValue('zip', address.zipcode, { shouldValidate: true });
                }}
                id={inputId}
                placeholder="Address"
                aria-invalid={fieldState.invalid}
              />
              {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
            </Field>
          );
        }}
      />
      <Controller
        name="addressLine2"
        control={control}
        render={({ field, fieldState }) => {
          const inputId = `${formId}-addressLine2`;

          return (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={inputId}>Address line 2</FieldLabel>
              <Input
                {...field}
                id={inputId}
                type="text"
                aria-invalid={fieldState.invalid}
                placeholder="Apt, floor, etc"
              />
              {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
            </Field>
          );
        }}
      />

      <Controller
        name="city"
        control={control}
        render={({ field, fieldState }) => {
          const inputId = `${formId}-city`;

          return (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={inputId}>City</FieldLabel>
              <Input
                {...field}
                id={inputId}
                type="text"
                aria-invalid={fieldState.invalid}
                placeholder="City"
              />
              {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
            </Field>
          );
        }}
      />

      <div className="flex gap-3">
        <Controller
          name="state"
          control={control}
          render={({ field, fieldState }) => {
            const inputId = `${formId}-state`;

            return (
              <Field data-invalid={fieldState.invalid} className="min-w-0">
                <FieldLabel htmlFor={inputId}>State</FieldLabel>
                <NativeSelect
                  {...field}
                  value={field.value ?? ''}
                  id={inputId}
                  aria-invalid={fieldState.invalid}
                >
                  <NativeSelectOption value="">State</NativeSelectOption>
                  {insuranceStateOptions.map((option) => (
                    <NativeSelectOption key={option.value} value={option.value}>
                      {option.value}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
              </Field>
            );
          }}
        />

        <Controller
          name="zip"
          control={control}
          render={({ field, fieldState }) => {
            const inputId = `${formId}-zip`;

            return (
              <Field data-invalid={fieldState.invalid} className="min-w-0">
                <FieldLabel htmlFor={inputId}>Zip</FieldLabel>
                <Input
                  {...field}
                  id={inputId}
                  type="text"
                  aria-invalid={fieldState.invalid}
                  placeholder="Zip"
                  maxLength={5}
                  inputMode="numeric"
                />
                {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
              </Field>
            );
          }}
        />
      </div>
    </div>
  );
}
